<script setup lang="ts">
import { useForwardExpose } from 'reka-ui';

interface Props {
	/**
	 * Width of the pinned content in px. Content is laid out at this width and clipped, so it does
	 * not reflow or scale while a `SplitPanel` animates the pane open, collapsed, or resized.
	 */
	size: number;
	/**
	 * Which edge the pane is pinned to — pass the SplitPanel's `primary`. Content stays glued to this
	 * (outer) edge, so it clips and reveals from the opposite (divider) edge during the transition.
	 */
	side?: 'start' | 'end';
}

// Content lands on the inner (pinned) element, not the structural wrapper.
defineOptions({ inheritAttrs: false });

const { side = 'start' } = defineProps<Props>();

// Expose the pinned content element (as `$el`) so consumers can use it as a teleport target.
const { forwardRef } = useForwardExpose();
</script>

<template>
	<div class="flex size-full overflow-x-hidden" :class="side === 'end' ? 'justify-end' : 'justify-start'">
		<div :ref="forwardRef" class="relative h-full shrink-0 overflow-x-hidden" :style="{ width: `${size}px` }" v-bind="$attrs">
			<slot />
		</div>
	</div>
</template>
